import React, {useMemo, useState} from 'react';
import type {SxProps} from '@mui/material';
import {useTheme} from '@mui/material';
import type {
  GridApiPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridInitialState,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import type {RowSelectionModel} from 'components/dataTable/types';
import type {Data, Schema} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {ThesaurusesGate} from 'services/thesaurus';
import type {DataTableProps} from 'components/dataTable';
import {DataTable} from 'components/dataTable';
import {Layout} from 'components/layout';
import {Spacer} from 'components/spacer';
import type {BasePartProps} from './types';
import {useSchemaThesaurusIds} from '../../hooks';
import {useTableConfigurationContext} from '../contexts/TableConfigurationContextType';
import {mergeWithDefaultColumnConfigs} from '../functions';
import {useDataTableColumns, useRowFunctions} from '../hooks';
import {mapToVisibilityModel} from '../mapper/mapToVisibilityModel';
import {QuickFilterButtons} from './QuickFilterButtons';

type TableSchemaProps<TData extends Data> = Omit<
  BasePartProps<undefined>,
  'part' | 'valuePath' | 'value' | 'relatedScope'
> & {
  header: React.ReactNode | string;
  schema: Schema;
  rows?: TData[];
  loading?: boolean;
  onRowClick?: (row: TData) => void;
  highlights?: string[];
  selection?: RowSelectionModel;
  onSelectionChanged?: (model: RowSelectionModel) => void;
  onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
  rowCount?: number;
  initialState?: GridInitialState;
  onSaveState?: (state: GridInitialState) => void;
  apiRef?: React.RefObject<GridApiPro | null>;
} & Pick<
    DataTableProps<TData>,
    'onPaginationModelChange' | 'onSortModelChange'
  >;

export function TableSchema<TData extends Data>({
  header,
  schema,
  rows,
  loading,
  onRowClick,
  highlights,
  rowCount,
  selection,
  onSelectionChanged,
  onRowsUpdate,
  initialState: storedSettings,
  onSaveState,
  apiRef: apiRefProp,
  onPaginationModelChange,
  onSortModelChange,
}: TableSchemaProps<TData>) {
  const {tLoose} = useLocalization();
  const {colors} = useTheme();
  const tableSx: SxProps = {
    p: 2,
    backgroundColor: colors.offWhite,
    borderRadius: 1,
    height: '100%',
    width: '100%',
    minHeight: '400px',
  };
  const {
    defaultColumns,
    indicateRowId,
    disableSorting,
    disableFiltering,
    disableFilteringByName,
    disableColumnResize,
    enableSelectionButton,
    contentHeight,
    partValueRenderers,
    partValueGetters,
    partValueDelimiter,
    autoRowHeight,
    quickFilters,
    enableQuickFilter,
    hidden,
    hideColumnSelector,
    pageSizeAllowAll,
    hideFooter,
    pinnedColumns,
    scrollAfterPagination,
    actions,
  } = useTableConfigurationContext();

  const thesaurusIds = useSchemaThesaurusIds(schema);

  const columnConfigs = useMemo(
    () => mergeWithDefaultColumnConfigs(schema, defaultColumns ?? []),
    [schema, defaultColumns],
  );

  const datatableColumns: GridColDef[] = useDataTableColumns(
    schema,
    columnConfigs,
    partValueRenderers,
    partValueGetters,
    partValueDelimiter,
    disableFiltering,
    disableFilteringByName,
    disableSorting,
    highlights,
    indicateRowId,
    actions,
  );

  const {getRowHeight} = useRowFunctions(schema, rows, autoRowHeight);

  // Only read by the grid on mount
  const initialState = useMemo<GridInitialState>(() => {
    const hiddenVisibilityModel = Object.entries(
      hidden ?? {},
    ).reduce<GridColumnVisibilityModel>((acc, [name, isHidden]) => {
      if (isHidden) {
        acc[name] = false;
      }
      return acc;
    }, {});

    return {
      ...storedSettings,
      columns: {
        ...storedSettings?.columns,
        columnVisibilityModel: {
          ...mapToVisibilityModel(columnConfigs),
          ...storedSettings?.columns?.columnVisibilityModel,
          // Hidden columns are never shown, regardless of stored settings
          ...hiddenVisibilityModel,
        },
      },
    };
  }, [columnConfigs, hidden, storedSettings]);

  // Grid owns the filter model, this only mirrors it for the quick filters
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    storedSettings?.filter?.filterModel,
  );

  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;

  return (
    <ThesaurusesGate thesauruses={thesaurusIds}>
      {enableQuickFilter ? (
        <>
          <QuickFilterButtons
            setFilterModel={model => apiRef.current?.setFilterModel(model)}
            onOpenFilterPanel={() => apiRef.current?.showFilterPanel()}
            quickFilters={quickFilters}
            filterModel={filterModel}
          />
          <Spacer size={3} />
        </>
      ) : null}
      <Layout flex={1} height={contentHeight} position="relative" sx={tableSx}>
        <DataTable
          apiRef={apiRef}
          key={schema.name}
          className={`datatable-${schema.name}`}
          columns={datatableColumns}
          rows={rows ?? []}
          loading={loading !== undefined ? loading : rows === undefined}
          getRowHeight={getRowHeight}
          onRowClick={onRowClick}
          pinnedColumns={pinnedColumns}
          initialState={initialState}
          onSaveState={onSaveState}
          onFilterModelChange={setFilterModel}
          onPaginationModelChange={onPaginationModelChange}
          onSortModelChange={onSortModelChange}
          disableColumnResize={disableColumnResize}
          enableSelectionButton={enableSelectionButton}
          selectionModel={selection}
          onSelectionChanged={onSelectionChanged}
          header={header}
          columnSelectionTitle={tLoose(`schema.table.columnselect.title`, {
            name: tLoose(`schema.name.${schema.name}.title`),
          })}
          rowCount={rowCount}
          hideFooter={hideFooter}
          onRowsUpdate={onRowsUpdate}
          pageSizeAllowAll={pageSizeAllowAll}
          scrollAfterPagination={scrollAfterPagination}
          slotProps={{
            toolbar: {
              hiddenColumns: hidden ? Object.keys(hidden) : undefined,
              hideColumnSelector,
            },
          }}
        />
      </Layout>
    </ThesaurusesGate>
  );
}
